import React, { createContext, useContext, useState, useEffect } from "react";

const CurrencyContext = createContext();

// Country code to currency code mapping
const COUNTRY_TO_CURRENCY = {
  PK: "PKR",
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  AU: "AUD",
  NZ: "NZD",
  AE: "AED",
  SA: "SAR",
  IN: "INR",
  DE: "EUR",
  FR: "EUR",
  IT: "EUR",
  ES: "EUR",
  NL: "EUR",
  BE: "EUR",
  AT: "EUR",
  IE: "EUR",
  FI: "EUR",
  PT: "EUR",
  GR: "EUR",
  KW: "KWD",
  OM: "OMR",
  QA: "QAR",
  BH: "BHD",
};

// Fallback exchange rates relative to 1 PKR
const FALLBACK_RATES_FROM_PKR = {
  PKR: 1,
  USD: 0.0036,
  EUR: 0.0033,
  GBP: 0.0028,
  AED: 0.0132,
  SAR: 0.0135,
  INR: 0.30,
  CAD: 0.0049,
  AUD: 0.0055,
};

// Currency symbols map
const CURRENCY_SYMBOLS = {
  PKR: "PKR ",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "AED ",
  SAR: "SAR ",
  INR: "₹",
  CAD: "CA$",
  AUD: "A$",
};

export const CurrencyProvider = ({ children }) => {
  const [country, setCountry] = useState("PK");
  const [currency, setCurrencyState] = useState("PKR");
  const [rates, setRates] = useState(FALLBACK_RATES_FROM_PKR);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function detectLocationAndRates() {
      try {
        // 1. Detect Country via IP API
        const locRes = await fetch("https://ipapi.co/json/", { cache: "force-cache" });
        if (locRes.ok) {
          const locData = await locRes.json();
          if (locData && locData.country_code && isMounted) {
            const detectedCountry = locData.country_code.toUpperCase();
            setCountry(detectedCountry);

            // Set currency based on country location, default to PKR
            const mappedCurrency = COUNTRY_TO_CURRENCY[detectedCountry] || (detectedCountry === "PK" ? "PKR" : "USD");
            setCurrencyState(mappedCurrency);
          }
        }
      } catch (err) {
        console.warn("[CurrencyContext] Location detection fallback:", err?.message);
      }

      try {
        // 2. Fetch Exchange Rates relative to PKR
        const rateRes = await fetch("https://open.er-api.com/v6/latest/PKR");
        if (rateRes.ok) {
          const rateData = await rateRes.json();
          if (rateData && rateData.rates && isMounted) {
            setRates({ ...FALLBACK_RATES_FROM_PKR, ...rateData.rates });
          }
        }
      } catch (err) {
        console.warn("[CurrencyContext] Using fallback exchange rates:", err?.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    detectLocationAndRates();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Convert an amount in PKR to target currency amount
   */
  const convertPrice = (pkrAmount, targetCurrency = currency) => {
    if (pkrAmount == null || isNaN(pkrAmount)) return 0;
    const num = Number(pkrAmount);
    if (num === 0) return 0;

    const rate = rates[targetCurrency] || FALLBACK_RATES_FROM_PKR[targetCurrency] || 1;
    if (targetCurrency === "PKR") return num;

    const converted = num * rate;
    return targetCurrency === "USD" || targetCurrency === "EUR" || targetCurrency === "GBP" || targetCurrency === "CAD" || targetCurrency === "AUD"
      ? Math.round(converted * 100) / 100
      : Math.round(converted);
  };

  /**
   * Format PKR amount into localized price string (e.g., "$5.40" or "PKR 1,500")
   */
  const formatPrice = (pkrAmount, targetCurrency = currency) => {
    if (pkrAmount == null || isNaN(pkrAmount)) return "";
    const num = Number(pkrAmount);
    if (num === 0) return "Free";

    const converted = convertPrice(num, targetCurrency);
    const symbol = CURRENCY_SYMBOLS[targetCurrency] || `${targetCurrency} `;

    if (targetCurrency === "PKR") {
      return `${symbol}${num.toLocaleString()}`;
    }

    const decimals = (targetCurrency === "USD" || targetCurrency === "EUR" || targetCurrency === "GBP") ? 2 : 0;
    return `${symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        country,
        rates,
        loading,
        convertPrice,
        formatPrice,
        symbols: CURRENCY_SYMBOLS,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};
